import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import {
  Form,
  FormControl,
  InputGroup,
  FormCheck,
  Row,
  Col,
} from 'react-bootstrap';
import WorkAreaOutput from './WorkAreaOutput';
import WorkAreaSelect from './WorkAreaSelect/WorkAreaSelect';
import ScientificNotationInput from './ScientificNotationInput';
import MultiSettingsInput from './MultiSettingsInput';
import WorkAreaFormGroup from './WorkAreaFormGroup';

export default class Input extends Component {
  onChange = (e) => {
    let inputValue = e.target.value;

    if (this.props.type === 'checkbox') {
      inputValue = e.target.checked ? true : false;
    }
    this.props.changeValue(this.props.name, inputValue);

    if (this.props.submitOnChange === 1 || this.props.submitOnChange === true) {
      let data = {
        [this.props.name]: inputValue,
      };
      this.props.submit(this.props.node, data);
    }
  };

  getValidationMessage = () => {
    if (this.props.type === 'number') {
      if (this.props.max !== null && this.props.value > this.props.max) {
        return (
          'value must not be greater than ' + this.props.max.toLocaleString()
        );
      }
      if (this.props.min !== null && this.props.value < this.props.min) {
        return 'value must not be less than ' + this.props.min.toLocaleString();
      }
      if (isNaN(this.props.value)) {
        return 'value must be a number';
      }
    }

    return '';
  };

  renderLabelText = () => {
    if (this.props.labelHTML) {
      return <span dangerouslySetInnerHTML={{ __html: this.props.label }} />;
    } else {
      return this.props.label;
    }
  };

  renderUnitsText = () => {
    if (this.props.units.length) {
      return <span dangerouslySetInnerHTML={{ __html: this.props.units }} />;
    } else {
      return null;
    }
  };

  renderSelect = (disabled) => {
    const isAdvanced =
      this.props.choices[0].hasOwnProperty('image') ||
      this.props.choices[0].hasOwnProperty('stats');

    return isAdvanced
      ? this.renderAdvancedSelect(disabled)
      : this.renderBasicSelect(disabled);
  };

  renderAdvancedSelect = (disabled) => {
    return (
      <WorkAreaSelect
        name={this.props.name}
        title={this.props.label}
        choices={this.props.choices}
        disabled={disabled}
        value={this.props.value}
        onChange={this.onChange}
        panelPosition={this.props.panelPosition}
      />
    );
  };

  renderBasicSelect = (disabled) => {
    const options = this.props.choices.map(function (choice, i) {
      return (
        <option key={i} value={choice.value}>
          {choice.label}
        </option>
      );
    });

    return (
      <Form.Select
        disabled={disabled}
        value={this.props.value}
        onChange={this.onChange}
        size="sm"
      >
        {options}
      </Form.Select>
    );
  };

  renderCheckbox = (disabled) => {
    return (
      <div className="text-end">
        <FormCheck
          id={this.props.node + '.' + this.props.name}
          disabled={disabled}
          onChange={this.onChange}
          checked={this.props.value === 1 || this.props.value === true}
        />
      </div>
    );
  };

  renderScientificNotationInput = (disabled) => {
    return (
      <InputGroup size="sm">
        <ScientificNotationInput
          {...this.props}
          disabled={disabled}
          onChange={(value) => this.props.changeValue(this.props.name, value)}
          aria-describedby={this.props.node + '.' + this.props.name + '_addon'}
          aria-label={this.props.name}
        />
        <InputGroup.Text
          id={this.props.node + '.' + this.props.name + '_addon'}
        >
          {this.renderUnitsText()}
        </InputGroup.Text>
      </InputGroup>
    );
  };

  renderInput = (disabled) => {
    let classes = 'sm';

    if (this.props.disableStepper) {
      classes += ' disable-stepper';
    }

    return (
      <InputGroup size="sm">
        <FormControl
          ref={(ref) => ref === 'target'}
          disabled={disabled}
          type="text"
          value={this.props.value}
          min={this.props.min}
          max={this.props.max}
          step={this.props.step}
          onChange={this.onChange}
          className={classes}
          aria-describedby={this.props.node + '.' + this.props.name + '_addon'}
          aria-label={this.props.name}
        />
        {!this.props.units.length || (
          <InputGroup.Text
            id={this.props.node + '.' + this.props.name + '_addon'}
          >
            {this.renderUnitsText()}
          </InputGroup.Text>
        )}
      </InputGroup>
    );
  };

  renderMultiSettingsInput = () => {
    const { value, ...rest } = this.props;
    return <MultiSettingsInput values={value} {...rest} />;
  };

  render() {
    let disabled = false;
    const helpText = this.getValidationMessage();
    const isValid = helpText.length === 0;

    // show this input depending on some condition
    if (this.props.showIf) {
      const { comparison, placeholder, value: showIfValue } = this.props.showIf;
      switch (comparison) {
        // show if the value is one of depValue
        case 'oneOf':
          let depValue = this.props.depValue;
          if (depValue === null) {
            depValue = 0;
          }

          if (showIfValue.indexOf(depValue) < 0) {
            return placeholder && placeholder.length ? (
              <WorkAreaOutput
                label={this.renderLabelText()}
                value={placeholder}
              />
            ) : null;
          }
          break;
        case 'eq':
          if (showIfValue !== this.props.depValue) {
            return placeholder && placeholder.length ? (
              <WorkAreaOutput
                label={this.renderLabelText()}
                value={placeholder}
              />
            ) : null;
          }
          break;
        case 'not':
          if (showIfValue === this.props.depValue) {
            return placeholder && placeholder.length ? (
              <WorkAreaOutput
                label={this.renderLabelText()}
                value={placeholder}
              />
            ) : null;
          }
          break;
        default:
          return null;
      }
    }

    // disable this input if some condition evaluates to true
    if (
      this.props.disableIf &&
      this.props.disableIf.comparison === 'oneOf' &&
      this.props.disableIf.value.indexOf(this.props.depValue) < 0
    ) {
      disabled = true;
    }

    let component;

    if (this.props.type === 'multiSettings') {
      component = this.renderMultiSettingsInput(disabled);

      return (
        <Form.Group
          controlId={this.props.name}
          className="m-0"
          validated={isValid ? null : 'error'}
        >
          {component}
        </Form.Group>
      );
    }

    if (this.props.type === 'select' || this.props.type === 'advancedSelect') {
      component = this.renderSelect(disabled);
    } else if (this.props.type === 'checkbox') {
      component = this.renderCheckbox(disabled);
    } else if (this.props.type === 'number' && this.props.convertAt) {
      component = this.renderScientificNotationInput(disabled);
    } else {
      component = this.renderInput(disabled);
    }

    return (
      <WorkAreaFormGroup
        name={this.props.name}
        isValid={isValid}
        helpText={helpText}
        label={this.renderLabelText()}
      >
        {component}
      </WorkAreaFormGroup>
    );
  }
}
Input.propTypes = {
  name: PropTypes.string,
  node: PropTypes.string,
  label: PropTypes.string,
  labelHTML: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  units: PropTypes.string,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  type: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  convertAt: PropTypes.number,
  precision: PropTypes.number,
  disableStepper: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  changeValue: PropTypes.func,
  showIf: PropTypes.object,
  disableIf: PropTypes.object,
  submitOnChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  submit: PropTypes.func,
};
Input.defaultProps = {
  name: '',
  node: '',
  label: '',
  labelHTML: false,
  units: '',
  required: false,
  value: '',
  type: 'text',
  min: null,
  max: null,
  convertAt: null,
  precision: null,
  step: null,
  disableStepper: false,
  changeValue: function (a, b) {},
  showIf: null,
  disableIf: null,
  submitOnChange: false,
  depValue: undefined,
};
